import * as ButtonPrimitive from "@kobalte/core/button";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { JSX, ValidComponent } from "solid-js";
import { splitProps } from "solid-js";
import { cn } from "~/util/utils.ts";

const buttonVariants = cva(
	"inline-flex items-center justify-center rounded-xl text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:bg-black/5 disabled:text-black/30 disabled:fill-black/30 active:[&:not(:disabled)]:translate-y-[2px] disabled:select-none disabled:cursor-not-allowed",
	{
		variants: {
			variant: {
				// other colors
				default: "border hover:bg-black/[2%]",
				transparent: "text-accent fill-accent",

				// accent colors
				accent: "bg-accent text-white fill-white hover:bg-accent/90",
				light: "bg-accent/10 fill-accent text-accent hover:bg-accent/5",
				outline: "border border-accent/20 text-accent fill-accent hover:bg-accent/5 disabled:bg-accent/5",
				subtle: "text-accent fill-accent hover:bg-accent/15",
				link: "text-primary underline-offset-4 hover:underline",

				// secondary colors
				secondary: "bg-secondary text-white fill-white hover:bg-secondary/90",
				"secondary-light": "bg-secondary/10 fill-secondary text-secondary hover:bg-secondary/5",
				"secondary-outline":
					"border border-secondary/20 text-secondary fill-secondary hover:bg-secondary/5 disabled:bg-secondary/5",
				"secondary-subtle": "text-secondary fill-secondary hover:bg-secondary/15",
				"secondary-link": "text-secondary underline-offset-4 hover:underline",

				// feedback colors
				success: "bg-green-600 text-white fill-white hover:bg-green-90",
				"success-light": "bg-green-100 text-green-600 fill-green-600 hover:bg-green-90",
				"success-outline":
					"border border-green-20 text-green-600 fill-green-600 hover:bg-green-5 disabled:bg-green-5",
				"success-subtle": "text-green-600 fill-green-600 hover:bg-green-15",
				"success-link": "text-green-600 underline-offset-4 hover:underline",

				warning: "bg-orange-600 text-white fill-white hover:bg-orange-90",
				"warning-light": "bg-orange-100 text-orange-600 fill-orange-600 hover:bg-orange-90",
				"warning-outline":
					"border border-orange-20 text-orange-600 fill-orange-600 hover:bg-orange-5 disabled:bg-orange-5",
				"warning-subtle": "text-orange-600 fill-orange-600 hover:bg-orange-15",
				"warning-link": "text-orange-600 underline-offset-4 hover:underline",

				destructive: "bg-destructive text-white hover:bg-destructive/90 fill-white",
				"destructive-light": "bg-destructive/10 text-destructive fill-destructive hover:bg-destructive/5",
				"destructive-outline":
					"border border-destructive/20 text-destructive fill-destructive hover:bg-destructive/5 disabled:bg-destructive/5",
				"destructive-subtle": "text-destructive fill-destructive hover:bg-destructive/15",
				"destructive-link": "text-destructive underline-offset-4 hover:underline",

				info: "bg-blue-600 text-white fill-white hover:bg-blue-90",
				"info-light": "bg-blue-100 text-blue-600 fill-blue-600 hover:bg-blue-90",
				"info-outline": "border border-blue-20 text-blue-600 fill-blue-600 hover:bg-blue-5 disabled:bg-blue-5",
				"info-subtle": "text-blue-600 fill-blue-600 hover:bg-blue-15",
				"info-link": "text-blue-600 underline-offset-4 hover:underline",
			},
			size: {
				default: "h-10 px-4 py-2",
				xs: "h-8 px-3",
				sm: "h-9 px-3",
				md: "h-10 px-4",
				lg: "h-11 px-8",
			},
			radius: {
				default: "rounded-md",
				xs: "rounded-xs",
				sm: "rounded-sm",
				md: "rounded-md",
				lg: "rounded-lg",
				xl: "rounded-xl",
				"2xl": "rounded-2xl",
			},
		},
		defaultVariants: {
			variant: "accent",
			size: "default",
			radius: "default",
		},
	},
);

type ButtonProps<T extends ValidComponent = "button"> = ButtonPrimitive.ButtonRootProps<T> &
	VariantProps<typeof buttonVariants> & {
		class?: string | undefined;
		children?: JSX.Element;
	} & JSX.HTMLAttributes<HTMLButtonElement>;

const Button = <T extends ValidComponent = "button">(props: PolymorphicProps<T, ButtonProps<T>>) => {
	const [local, others] = splitProps(props as ButtonProps, ["variant", "size", "class"]);

	return (
		<ButtonPrimitive.Root
			class={cn(buttonVariants({ variant: local.variant, size: local.size }), local.class)}
			{...others}
		/>
	);
};

export type { ButtonProps };
export { Button, buttonVariants };
